import { PositionSide } from '@kwenta/sdk/types'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { FC, memo, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import Error, { ErrorView } from 'components/ErrorView'
import Spacer from 'components/Spacer'
import Connector from 'containers/Connector'
import { setOpenModal } from 'state/app/reducer'
import { selectLeverageSide, selectTradeOrderType } from 'state/futures/common/selectors'
import { setTradePanelDrawerOpen } from 'state/futures/reducer'
import { selectProfitCalculatorVisible } from 'state/futures/selectors'
import {
	changeCrossMarginLeverageSide,
	clearCrossMarginTradeInputs,
} from 'state/futures/snxPerpsV3/actions'
import {
	selectCondOrdersEnabled,
	selectCrossMarginAccount,
	selectShowSwitchNetworkCrossMargin,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectPricesConnectionError } from 'state/prices/selectors'

import LeverageInput from '../LeverageInput'
import OrderSizing from '../OrderSizing'
import PositionButtons from '../PositionButtons'
import { ProfitCalculatorContent as ProfitCalculator } from '../ProfitCalculator/ProfitCalculator'

import CloseOnlyPrompt from './CloseOnlyPrompt'
import CrossMarginTradePanelPreview from './CrossMarginTradePanelPreview'
import FuturesUnsupportedNetwork from './FuturesUnsupported'
import { TradePanelOrderTypeSelector } from './OrderTypeSelector'
import SLTPInputs from './SLTPInputs'
import SubmitCrossMarginTradeButton from './SubmitCrossMarginTrade'
import TradeBalanceCrossMargin from './TradeBalanceCrossMargin'
import { NetworkSwitchContainer, TradePanelContainer } from './TradePanelContainer'
import TradePanelPriceInput from './TradePanelPriceInput'

type Props = {
	mobile?: boolean
	closeDrawer?: () => void
}

const TradePanelCrossMargin: FC<Props> = memo(({ mobile, closeDrawer }) => {
	const dispatch = useAppDispatch()
	const { t } = useTranslation()

	const { isWalletConnected } = Connector.useContainer()
	const { openConnectModal } = useConnectModal()
	const leverageSide = useAppSelector(selectLeverageSide)
	const pricesConnectionError = useAppSelector(selectPricesConnectionError)
	const crossMarginAccount = useAppSelector(selectCrossMarginAccount)
	const showNetworkPrompt = useAppSelector(selectShowSwitchNetworkCrossMargin)
	const orderType = useAppSelector(selectTradeOrderType)
	const profitCalculatorVisible = useAppSelector(selectProfitCalculatorVisible)
	const condOrdersEnabled = useAppSelector(selectCondOrdersEnabled)

	useEffect(() => {
		dispatch(clearCrossMarginTradeInputs())
	}, [dispatch])

	const handleChangeSide = useCallback(
		(side: PositionSide) => {
			dispatch(changeCrossMarginLeverageSide(side))
		},
		[dispatch]
	)

	const handleConnectOrCreateAccount = useCallback(() => {
		if (!isWalletConnected) {
			openConnectModal?.()
		}

		if (!crossMarginAccount && isWalletConnected) {
			dispatch(setTradePanelDrawerOpen(false))
			dispatch(setOpenModal('futures_cross_margin_onboard'))
		}
	}, [isWalletConnected, crossMarginAccount, dispatch, openConnectModal])

	const tradeInputs = useMemo(() => {
		if (orderType !== 'market' && !condOrdersEnabled) {
			return <ErrorView messageType="warn" message={`${orderType} orders will be available soon`} />
		}
		return (
			<>
				{orderType !== 'market' && (
					<>
						<TradePanelPriceInput />
						<Spacer height={16} />
					</>
				)}
				<OrderSizing />
				<LeverageInput />
				<SLTPInputs />
				{!crossMarginAccount || !isWalletConnected ? (
					<>
						<StyledTransactionButton
							data-testid="open-cross-margin-onboard-modal"
							onClick={handleConnectOrCreateAccount}
							variant="yellow"
							fullWidth
							size="medium"
						>
							{isWalletConnected
								? t('futures.modals.onboard.create-account')
								: t('common.wallet.connect-wallet')}
						</StyledTransactionButton>
						<Spacer height={15} />
					</>
				) : (
					<SubmitCrossMarginTradeButton />
				)}
				<CrossMarginTradePanelPreview />
			</>
		)
	}, [
		handleConnectOrCreateAccount,
		t,
		isWalletConnected,
		crossMarginAccount,
		orderType,
		condOrdersEnabled,
	])

	if (showNetworkPrompt) {
		return (
			<NetworkSwitchContainer $mobile={mobile} data-testid="cross-margin-trade-panel">
				<FuturesUnsupportedNetwork />
			</NetworkSwitchContainer>
		)
	}

	return (
		<StyledTradePanelContainer $mobile={mobile} data-testid="cross-margin-trade-panel">
			{process.env.NEXT_PUBLIC_CLOSE_ONLY === 'true' ? (
				<CloseOnlyPrompt $mobile={mobile} />
			) : (
				<>
					<PositionButtons
						selected={leverageSide}
						onSelect={handleChangeSide}
						mobile={mobile}
						closeDrawer={closeDrawer}
					/>

					<MainPanelContent $mobile={mobile} profitCalculatorVisible={profitCalculatorVisible}>
						{
							// TODO: Share across trade panels
							pricesConnectionError && <Error message="Failed to connect to price feed." />
						}

						{profitCalculatorVisible ? (
							<ProfitCalculator />
						) : (
							<>
								<TradePanelOrderTypeSelector />
								<Spacer height={16} />
								{tradeInputs}
							</>
						)}
					</MainPanelContent>
				</>
			)}
			{!mobile && !!crossMarginAccount && (
				<TradeBalanceContainer>
					<TradeBalanceCrossMargin />
				</TradeBalanceContainer>
			)}
		</StyledTradePanelContainer>
	)
})

const StyledTransactionButton = styled(TransactionButton)`
	height: 44px;
`

const MainPanelContent = styled.div<{ profitCalculatorVisible: boolean; $mobile?: boolean }>`
	padding: 0 15px;
	padding-top: 16px;
	${({ profitCalculatorVisible }) => !profitCalculatorVisible && 'padding-bottom: 60px'};
	width: 100%;
	height: 100%;
	overflow: scroll;
	position: relative;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.containers.primary.background};
	${(props) =>
		props.$mobile &&
		css`
			padding: 65px 15px 0;
		`}
`

const TradeBalanceContainer = styled.div`
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	z-index: 10;
	width: 100%;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.containers.primary.background};
`

const StyledTradePanelContainer = styled(TradePanelContainer)`
	display: flex;
	flex-direction: column;
`

export default TradePanelCrossMargin
